import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Sun, Moon, LayoutGrid, Zap, TrendingUp, Shield, ArrowRight, Refrigerator } from 'lucide-react';
import { motion } from 'framer-motion';

export const LandingPage = () => {
  const { theme, toggleTheme } = useTheme();
  const { user } = useAuth();

  const features = [
    {
      icon: Zap,
      title: 'Attention-Based Prioritization',
      description: 'Focus on what truly impacts your wellbeing, not just deadlines.'
    },
    {
      icon: TrendingUp,
      title: 'Track Your Progress',
      description: 'Visual analytics show your achievements and productivity trends.'
    },
    {
      icon: Shield,
      title: 'Wellbeing First',
      description: 'Balance work, personal, and social interests for a better life.'
    }
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* Header */}
      <header className="flex items-center justify-between p-4 md:p-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
            <LayoutGrid className="w-5 h-5 text-primary-foreground" />
          </div>
          <span className="text-xl font-bold font-['Manrope']">Brioritizer</span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="theme-toggle">
            {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </Button>
          {user ? (
            <Link to="/dashboard">
              <Button data-testid="go-dashboard-btn">Go to Dashboard</Button>
            </Link>
          ) : (
            <Link to="/login">
              <Button data-testid="get-started-btn">Get Started</Button>
            </Link>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <main className="flex-1 flex flex-col items-center justify-center px-4 py-12 md:py-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="text-center max-w-3xl mx-auto"
        >
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold font-['Manrope'] tracking-tight mb-6">
            Prioritize Your
            <span className="block text-emerald-500">Wellbeing</span>
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-2xl mx-auto leading-relaxed">
            Beyond task management — Brioritizer helps you focus on what truly matters: 
            your attention, impact, and overall wellbeing.
          </p>
          
          {!user && (
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register">
                <Button size="lg" className="h-14 px-8 text-lg rounded-full gap-2" data-testid="hero-cta-btn">
                  Start Brioritizing
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </Link>
              <Link to="/login">
                <Button size="lg" variant="outline" className="h-14 px-8 text-lg rounded-full" data-testid="hero-signin-btn">
                  Sign In
                </Button>
              </Link>
            </div>
          )}
        </motion.div>

        {/* Matrix Preview */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          className="mt-12 w-full max-w-4xl mx-auto"
        >
          {/* Axis Labels */}
          <div className="flex items-center justify-center mb-2">
            <span className="text-sm md:text-base font-bold text-primary tracking-wide">IMPACT</span>
          </div>
          
          <div className="flex items-stretch gap-2">
            {/* Y-axis label */}
            <div className="flex flex-col items-center justify-center">
              <span className="text-sm md:text-base font-bold text-primary tracking-wide" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>ATTENTION</span>
            </div>
            
            {/* Matrix */}
            <div 
              className="relative flex-1 aspect-[4/3] md:aspect-[16/9] rounded-3xl overflow-hidden border border-white/20 dark:border-white/10 shadow-2xl"
              style={{
                background: theme === 'dark' 
                  ? 'radial-gradient(circle at center, #2a2a2f 0%, #1a1d21 100%)'
                  : 'radial-gradient(circle at center, #ffffff 0%, #f5f5f0 100%)'
              }}
            >
              {/* Matrix Grid with correct labels */}
              <div className="absolute inset-0 grid grid-cols-2 grid-rows-2">
                {/* Q1: Top Left - Green (High Attention / High Impact) */}
                <div 
                  className="relative flex items-start justify-start p-3"
                  style={{
                    background: theme === 'dark'
                      ? 'radial-gradient(circle at top left, rgba(16, 185, 129, 0.35), transparent 70%)'
                      : 'radial-gradient(circle at top left, rgba(16, 185, 129, 0.4), transparent 70%)'
                  }}
                >
                  <span className="text-emerald-600 dark:text-emerald-400 font-medium text-xs md:text-sm">
                    High Attention / High Impact
                  </span>
                </div>
                
                {/* Q2: Top Right - Amber (High Attention / Low Impact) */}
                <div 
                  className="relative flex items-start justify-end p-3"
                  style={{
                    background: theme === 'dark'
                      ? 'radial-gradient(circle at top right, rgba(245, 158, 11, 0.35), transparent 70%)'
                      : 'radial-gradient(circle at top right, rgba(245, 158, 11, 0.4), transparent 70%)'
                  }}
                >
                  <span className="text-amber-600 dark:text-amber-400 font-medium text-xs md:text-sm text-right">
                    High Attention / Low Impact
                  </span>
                </div>
                
                {/* Q3: Bottom Left - Amber (Low Attention / High Impact) */}
                <div 
                  className="relative flex items-end justify-start p-3"
                  style={{
                    background: theme === 'dark'
                      ? 'radial-gradient(circle at bottom left, rgba(245, 158, 11, 0.35), transparent 70%)'
                      : 'radial-gradient(circle at bottom left, rgba(245, 158, 11, 0.4), transparent 70%)'
                  }}
                >
                  <span className="text-amber-600 dark:text-amber-400 font-medium text-xs md:text-sm">
                    Low Attention / High Impact
                  </span>
                </div>
                
                {/* Q4: Bottom Right - Red (Low Attention / Low Impact) */}
                <div 
                  className="relative flex items-end justify-end p-3"
                  style={{
                    background: theme === 'dark'
                      ? 'radial-gradient(circle at bottom right, rgba(244, 63, 94, 0.35), transparent 70%)'
                      : 'radial-gradient(circle at bottom right, rgba(244, 63, 94, 0.4), transparent 70%)'
                  }}
                >
                  <span className="text-rose-600 dark:text-rose-400 font-medium text-xs md:text-sm text-right">
                    Low Attention / Low Impact
                  </span>
                </div>
              </div>
              
              {/* Grid lines */}
              <div className="absolute inset-0 pointer-events-none">
                <div className="absolute left-1/2 top-0 bottom-0 w-px bg-border/30" />
                <div className="absolute top-1/2 left-0 right-0 h-px bg-border/30" />
              </div>
              
              {/* Center button preview */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-primary shadow-lg flex items-center justify-center ring-2 ring-background/80">
                  <span className="text-primary-foreground text-xl">+</span>
                </div>
                <p className="text-center text-[8px] md:text-[10px] text-muted-foreground mt-1 whitespace-nowrap">
                  Add interest
                </p>
              </div>
              
              {/* Sample interest cards distributed across quadrants */}
              {/* Q1: High Attention / High Impact (top-left) */}
              <div className="absolute top-[15%] left-[8%] px-2 py-1 rounded-lg bg-card/80 border border-border shadow-sm text-xs">
                Work out
              </div>
              <div className="absolute top-[30%] left-[22%] px-2 py-1 rounded-lg bg-card/80 border border-border shadow-sm text-xs">
                Call Mum
              </div>
              <div className="absolute top-[22%] left-[35%] px-2 py-1 rounded-lg bg-card/80 border border-border shadow-sm text-xs hidden sm:block">
                Make doctor's appointment
              </div>
              <div className="absolute top-[38%] left-[10%] px-2 py-1 rounded-lg bg-card/80 border border-border shadow-sm text-xs">
                Write report
              </div>

              {/* Q2: High Attention / Low Impact (top-right) */}
              <div className="absolute top-[18%] right-[8%] px-2 py-1 rounded-lg bg-card/80 border border-border shadow-sm text-xs">
                Fix toaster
              </div>
              <div className="absolute top-[32%] right-[22%] px-2 py-1 rounded-lg bg-card/80 border border-border shadow-sm text-xs hidden sm:block">
                Respond to lawyer
              </div>
              <div className="absolute top-[26%] right-[38%] px-2 py-1 rounded-lg bg-card/80 border border-border shadow-sm text-xs">
                Food shop
              </div>

              {/* Q3: Low Attention / High Impact (bottom-left) */}
              <div className="absolute bottom-[28%] left-[8%] px-2 py-1 rounded-lg bg-card/80 border border-border shadow-sm text-xs">
                Bank reconciliation
              </div>
              <div className="absolute bottom-[35%] left-[32%] px-2 py-1 rounded-lg bg-card/80 border border-border shadow-sm text-xs hidden sm:block">
                Buy kids new shoes
              </div>

              {/* Q4: Low Attention / Low Impact (bottom-right) */}
              <div className="absolute bottom-[30%] right-[10%] px-2 py-1 rounded-lg bg-card/80 border border-border shadow-sm text-xs">
                Edit website
              </div>
              <div className="absolute bottom-[17%] right-[28%] px-2 py-1 rounded-lg bg-card/80 border border-border shadow-sm text-xs">
                Pay grocer
              </div>
              <div className="absolute bottom-[22%] right-[42%] px-2 py-1 rounded-lg bg-card/80 border border-border shadow-sm text-xs hidden sm:block">
                Organise date night
              </div>
              
              {/* Achievement store preview */}
              <div className="absolute top-2 left-1/2 -translate-x-1/2 flex items-center gap-1 px-2 py-1 rounded-lg border border-dashed border-blue-300 dark:border-blue-700 bg-blue-50/80 dark:bg-blue-950/50">
                <Refrigerator className="w-4 h-4 text-blue-500" />
                <span className="text-[10px] font-medium text-blue-600 dark:text-blue-400">Achievements</span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Features */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto w-full"
        >
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.5 + index * 0.1 }}
              className="glass rounded-2xl p-6 border border-white/20 dark:border-white/10"
            >
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                <feature.icon className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-semibold font-['Manrope'] text-lg mb-2">{feature.title}</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">{feature.description}</p>
            </motion.div>
          ))}
        </motion.div>
      </main>

      {/* Footer */}
      <footer className="py-6 px-4 text-center text-sm text-muted-foreground border-t border-border space-y-2">
        <p>&copy; 2026 Brioritizer. Your wellbeing comes first.</p>
        <a href="/privacy" className="text-primary hover:underline" data-testid="landing-privacy-link">Privacy Notice</a>
      </footer>
    </div>
  );
};
